import React, { useCallback, useMemo } from "react";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";

import { Table, Button, OverflowTooltip } from "components";
import { renderComparisonValue } from "mappers";
import { uiActions } from "store";
import { ColumnTypes } from "types";

import styles from "./matchReport.module.scss";

type ComparisonReportTableProps = {
	data: { [key: string]: any }[];
	disableHeader?: boolean;
	isLoading?: boolean;
	isOwnersReport?: boolean;
	titleColumn?: string;
	index: number;
};

const ComparisonReportTable = ({
	data = [],
	titleColumn = "",
	disableHeader = false,
	isLoading = false,
	isOwnersReport = false,
	index,
}: ComparisonReportTableProps) => {
	const dispatch = useDispatch();

	const DisplayValue = useCallback(({ value, className }) => {
		const renderValue = renderComparisonValue(value);
		return (
			<span
				className={`${className} p-0 ${styles.customEllipsisForColumn}`}
			>
				<span>{renderValue}</span>
			</span>
		);
	}, []);

	const handleAcquirerMatchResultClick = useCallback(
		(e, result) => {
			e.stopPropagation();
			dispatch(
				uiActions.showModal("ACQUIRER_MATCH_CONTACT", {
					acquirerData: result,
				}),
			);
		},
		[dispatch],
	);

	const columns: ColumnTypes = useMemo(
		() => [
			{
				Header: `${
					index === 0
						? `${isOwnersReport ? "Owner Results" : ""}`
						: ""
				}`,
				accessor: "name",
				disableSortBy: true,
				classNameHeader: `${styles.reportHeader} `,
				classNameCell: clsx("text-muted", "pl-3", styles.reportHeader),
			},
			{
				Header: `${!isOwnersReport && index === 0 ? "Inquiry" : ""}`,
				accessor: "inquiry",
				disableSortBy: true,
				classNameCell: "pr-3",
				Cell({ value, row: { original } }) {
					const className = original.isMatch ? styles.highlight : "";
					return (
						<DisplayValue
							className={className}
							value={value.data}
						/>
					);
				},
			},

			{
				Header: ({ initialRows }) => {
					const name = `${
						initialRows[0]?.original?.acquirerData?.first_name || ""
					} ${
						initialRows[0]?.original?.acquirerData?.last_name || ""
					}`;
					return (
						<div className={styles.matchResultsHeader}>
							<p className={`${styles.mainTitle} mb-0`}>
								{!isOwnersReport && index === 0
									? titleColumn
									: ""}
							</p>
							{titleColumn === "Merchant MATCH Results" && (
								<>
									{!isEmpty(
										initialRows[0].original?.acquirerData,
									) && (
										<div className="d-flex align-items-center">
											<span
												className={`${styles.addedByTitle} mb-0`}
											>
												Added by
											</span>
											<OverflowTooltip
												title={"test Name"}
											>
												<Button
													className={`${styles.addedName} px-0`}
													minWidth="auto"
													variant="secondary"
													value={name}
													iconPosition="right"
													onClick={e =>
														handleAcquirerMatchResultClick(
															e,
															initialRows[0]
																.original
																?.acquirerData,
														)
													}
												/>
											</OverflowTooltip>
										</div>
									)}
								</>
							)}
						</div>
					);
				},
				accessor: "merchantMatchResults",
				disableSortBy: true,
				classNameCell: `${styles.border} ${"mr-3"}`,
				Cell({ value, row: { original } }) {
					const className = original.isMatch ? styles.highlight : "";
					return (
						<DisplayValue
							className={className}
							value={value.data}
						/>
					);
				},
			},
		],
		[
			index,
			isOwnersReport,
			DisplayValue,
			titleColumn,
			handleAcquirerMatchResultClick,
		],
	);

	return (
		<Table
			disableHeader={disableHeader}
			columns={columns}
			data={data}
			bordered="horizontal"
			hideFooter
			compact
			meta={{ loading: isLoading, totalItems: 0 }}
		/>
	);
};

export default ComparisonReportTable;
